import React, { useState, useEffect } from 'react';
// import { Link } from 'react-router-dom';
import { FiTrendingUp, FiCheck, FiTarget, FiBarChart2, FiActivity, FiEye, FiZap, FiPieChart, FiArrowRight, FiMail } from 'react-icons/fi';
import Footer from '../../components/Footer';
import EmailGate from '../../components/EmailGate';
import { subscribe } from '../../api/subscriberApi';

const ACCENT = '#8000ff';
const GLOW = 'rgba(128,0,255,0.15)';

const RAINBOW = [
  '#ff0040','#ff4000','#ff8000','#ffc000',
  '#ffff00','#80ff00','#00ff80','#00ffff',
  '#0080ff','#4000ff','#8000ff','#ff00ff',
];

function rainbowText(text) {
  const chars = text.split('');
  const vis = chars.filter(c => c !== ' ').length;
  let ci = 0;
  return chars.map((ch, i) => {
    if (ch === ' ') return <span key={i}>&nbsp;</span>;
    const color = RAINBOW[Math.floor((ci / vis) * RAINBOW.length) % RAINBOW.length];
    ci++;
    return <span key={i} style={{ color }}>{ch}</span>;
  });
}

function TypingDemo({ texts, interval = 4000 }) {
  const [idx, setIdx] = useState(0);
  const [shown, setShown] = useState(0);
  const [erasing, setErasing] = useState(false);

  useEffect(() => {
    const text = texts[idx];
    if (!erasing) {
      if (shown < text.length) {
        const t = setTimeout(() => setShown(s => s + 1), 30 + Math.random() * 25);
        return () => clearTimeout(t);
      }
      const t = setTimeout(() => setErasing(true), interval);
      return () => clearTimeout(t);
    } else {
      if (shown > 0) {
        const t = setTimeout(() => setShown(s => s - 1), 15);
        return () => clearTimeout(t);
      }
      setErasing(false);
      setIdx(i => (i + 1) % texts.length);
    }
  }, [shown, erasing, idx, texts, interval]);

  return <span>{texts[idx].slice(0, shown)}<span className="pp2-cursor">|</span></span>;
}

const features = [
  { icon: <FiActivity />, title: 'Real-Time Detection', desc: 'Monitor emerging trends across social media, news, forums, and search data with AI-powered signal detection.', tag: 'LIVE' },
  { icon: <FiTarget />, title: 'Niche Scoring', desc: 'Evaluate market niches on growth trajectory, competition density, and revenue potential.', tag: 'ALGO' },
  { icon: <FiBarChart2 />, title: 'Competitive Intel', desc: 'Track competitor movements, product launches, and market positioning with automated alerts.', tag: 'INTEL' },
  { icon: <FiEye />, title: 'Opportunity Mapping', desc: 'AI identifies underserved market segments and ranks them by accessibility and growth potential.', tag: 'MAP' },
  { icon: <FiPieChart />, title: 'Market Dynamics', desc: 'Visualize market share shifts and forecast changes with predictive models.', tag: 'VIZ' },
  { icon: <FiZap />, title: 'Auto Reports', desc: 'Weekly intelligence reports generated automatically with actionable recommendations.', tag: 'AUTO' },
];

const betaPerks = [
  'Full access to all trend analysis features',
  'Shape the product with direct feedback to our team',
  'Priority support during the beta period',
  'Locked-in free tier when we launch publicly',
];

const demoQueries = [
  'emerging AI niches with low competition and >40% growth',
  'SaaS market trends in developer tooling Q4 2024',
  'competitor analysis: Notion vs Obsidian vs Logseq',
  'rising demand signals for AI code review tools',
];

export default function TrendScanPage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleBetaSignup = (e, gUser) => {
    e.preventDefault();
    const finalEmail = gUser?.email || email;
    if (!finalEmail.trim()) return;
    subscribe({ email: finalEmail, name: gUser?.name, picture: gUser?.picture, source: 'trendscan-beta', group: 'beta-trendscan' });
    setSubmitted(true);
  };

  return (
    <div className="product-page pp2-page" style={{'--pp-accent': ACCENT, '--pp-glow': GLOW}}>
      {/* Hero */}
      <section className="pp2-hero">
        <div className="pp2-hero-inner">
          <div className="pp2-badge"><FiTrendingUp /> TrendScan</div>
          <h1 className="pp2-title">{rainbowText('TrendScan')}</h1>
          <p className="pp2-tagline-label">Trend Analysis Engine</p>
          <p className="pp2-subtitle">
            See market shifts before they happen. Turn noise into actionable intelligence.
          </p>
          <div className="pp2-beta-banner">
            <span className="pp2-beta-tag">BETA</span>
            <span>Now accepting early access signups — free during beta</span>
          </div>
          <div className="pp2-actions">
            <a href="#beta-signup" className="pp2-btn-primary">Join the Beta</a>
            <button className="pp2-btn-ghost">Learn More <FiArrowRight /></button>
          </div>
        </div>
      </section>

      {/* Search demo */}
      <section className="pp2-demo-section">
        <div className="pp2-demo-bar">
          <FiTrendingUp className="pp2-demo-icon" />
          <span className="pp2-demo-text">
            <TypingDemo texts={demoQueries} interval={3000} />
          </span>
        </div>
      </section>

      {/* Features */}
      <section className="pp2-features">
        <div className="pp2-features-inner">
          <h2>Intelligence, not just data</h2>
          <p className="pp2-features-sub">TrendScan doesn't just show charts — it tells you what to do about them.</p>
          <div className="pp2-features-grid">
            {features.map(f => (
              <div key={f.title} className="pp2-feature-card">
                <div className="pp2-feature-head">
                  <div className="pp2-feature-icon">{f.icon}</div>
                  <span className="pp2-feature-tag">{f.tag}</span>
                </div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Beta Signup */}
      <section className="pp2-beta-section" id="beta-signup">
        <div className="pp2-beta-inner">
          <h2>Get Early Access</h2>
          <p className="pp2-beta-sub">
            TrendScan is in private beta. Sign up to get access — no credit card, no commitment.
            Beta users get free access and a direct line to shape the product.
          </p>
          <div className="pp2-beta-perks">
            {betaPerks.map(perk => (
              <div key={perk} className="pp2-beta-perk">
                <FiCheck /> <span>{perk}</span>
              </div>
            ))}
          </div>
          {!submitted ? (
            <EmailGate prompt="Sign in with Google to join the beta — we only use your email.">
              {(gUser) => (
                <form className="pp2-beta-form" onSubmit={(e) => handleBetaSignup(e, gUser)}>
                  <div className="pp2-beta-input-wrap">
                    <FiMail className="pp2-beta-input-icon" />
                    <input
                      type="email"
                      placeholder="you@example.com"
                      value={gUser.email || email}
                      onChange={e => setEmail(e.target.value)}
                      required
                      readOnly={!!gUser.email}
                    />
                  </div>
                  <button type="submit" className="pp2-btn-primary">Request Beta Access</button>
                </form>
              )}
            </EmailGate>
          ) : (
            <div className="pp2-beta-success">
              <FiCheck /> <span>You're on the list! We'll reach out soon with your beta invite.</span>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
